import { IconAlertCircle, IconArrowLeft, IconFileOff, IconRefresh } from '@tabler/icons-react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { MarkdownRenderer } from './MarkdownRenderer';
import { TableOfContents } from './TableOfContents';
import { Breadcrumbs } from './Breadcrumbs';
import { DocumentData } from '../hooks/useDocumentation';

/**
 * Props for the DocumentationContentWithTOC component
 */
interface DocumentationContentWithTOCProps {
  /** The document data to display */
  doc?: DocumentData;
  /** Loading state */
  loading: boolean;
  /** Error object if an error occurred */
  error?: Error;
  /** Organization ID for breadcrumb navigation */
  organizationId: string;
  /** Current document path for breadcrumbs */
  docPath?: string;
  /** Whether to show table of contents (default: true) */
  showToc?: boolean;
  /** Optional CSS class name */
  className?: string;
  /** Callback to retry loading */
  onRetry?: () => void;
  /** The requested document does not exist (TELV1-604) */
  notFound?: boolean;
  /** Navigate to the documentation home, offered on the not-found state */
  onGoHome?: () => void;
}

/**
 * DocumentationContentWithTOC component
 *
 * @description
 * Enhanced version of DocumentationContent that includes Table of Contents and Breadcrumbs.
 * Provides a complete documentation viewing experience with navigation aids.
 *
 * Features:
 * - Breadcrumb navigation showing document hierarchy
 * - Main content area with markdown rendering
 * - Floating table of contents (desktop only)
 * - 2-column layout on large screens: [content] [toc]
 * - 1-column layout on mobile/tablet
 * - All states handled: loading, error, empty, content
 * - Theme-aware styling
 * - Responsive design
 * - Accessibility features
 *
 * Layout:
 * - Mobile/Tablet: Single column with full-width content
 * - Desktop: Two columns with content (max-w-4xl) and sticky TOC
 * - TOC automatically hidden if showToc is false or no headings found
 *
 * @example
 * ```tsx
 * function DocumentationPage() {
 *   const { organization_id, docPath } = useParams();
 *   const { document, isLoading, error } = useDocumentation(docPath);
 *
 *   return (
 *     <DocumentationContentWithTOC
 *       doc={document}
 *       loading={isLoading}
 *       error={error}
 *       organizationId={organization_id}
 *       docPath={docPath}
 *       showToc={true}
 *       onRetry={() => window.location.reload()}
 *     />
 *   );
 * }
 * ```
 */
export function DocumentationContentWithTOC({
  doc,
  loading,
  error,
  organizationId,
  docPath,
  showToc = true,
  className,
  onRetry,
  notFound = false,
  onGoHome,
}: DocumentationContentWithTOCProps) {
  const { currentTheme } = useTheme();

  /**
   * Renders loading skeleton
   */
  const renderLoading = () => (
    <div className="space-y-6" role="status" aria-label="Loading documentation">
      {/* Breadcrumb skeleton */}
      <Skeleton className="h-6 w-96" />

      {/* Title skeleton */}
      <Skeleton className="h-10 w-3/4" />

      {/* Content skeletons */}
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>

      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-4/6" />
      </div>

      <Skeleton className="h-32 w-full" />

      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
      </div>
    </div>
  );

  /**
   * Renders error state
   */
  const renderError = () => (
    <div
      className={cn('flex flex-col items-center justify-center py-12 px-4', 'text-center')}
      role="alert"
      aria-live="assertive"
    >
      <div
        className="rounded-full p-3 mb-4"
        style={{ backgroundColor: `${currentTheme?.primary}20` }}
      >
        <IconAlertCircle
          size={48}
          style={{ color: currentTheme?.primary || '#096846' }}
          aria-hidden="true"
        />
      </div>

      <h2 className="text-2xl font-semibold mb-2" style={{ color: currentTheme?.['primary-text'] }}>
        Error Loading Document
      </h2>

      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
        {error?.message || 'Failed to load the documentation. Please try again.'}
      </p>

      {onRetry && (
        <Button
          onClick={onRetry}
          variant="default"
          className="flex items-center gap-2"
          aria-label="Retry loading document"
        >
          <IconRefresh size={18} aria-hidden="true" />
          Retry
        </Button>
      )}
    </div>
  );

  /**
   * Renders not-found state (TELV1-604): an old bookmark or a mistyped link.
   * No Retry — retrying cannot make a missing page appear.
   */
  const renderNotFound = () => (
    <div
      className={cn('flex flex-col items-center justify-center py-16 px-4', 'text-center')}
      role="alert"
    >
      <div
        className="rounded-full p-3 mb-4"
        style={{ backgroundColor: `${currentTheme?.primary}10` }}
      >
        <IconFileOff size={48} className="text-gray-400" aria-hidden="true" />
      </div>

      <h2 className="text-2xl font-semibold mb-2" style={{ color: currentTheme?.['primary-text'] }}>
        Page Not Found
      </h2>

      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md">
        This documentation page doesn&apos;t exist. It may have been moved or removed.
      </p>

      {onGoHome && (
        <Button onClick={onGoHome} variant="default" className="flex items-center gap-2">
          <IconArrowLeft size={18} aria-hidden="true" />
          Go to documentation home
        </Button>
      )}
    </div>
  );

  /**
   * Renders empty state (no document selected)
   */
  const renderEmpty = () => (
    <div className={cn('flex flex-col items-center justify-center py-16 px-4', 'text-center')}>
      <div
        className="rounded-full p-3 mb-4"
        style={{ backgroundColor: `${currentTheme?.primary}10` }}
      >
        <IconFileOff size={48} className="text-gray-400" aria-hidden="true" />
      </div>

      <h2 className="text-2xl font-semibold mb-2" style={{ color: currentTheme?.['primary-text'] }}>
        No Document Selected
      </h2>

      <p className="text-gray-600 dark:text-gray-400 max-w-md">
        Select a documentation page from the sidebar to view its content.
      </p>
    </div>
  );

  /**
   * Renders document content with TOC
   */
  const renderContent = () => (
    <div className="flex gap-8">
      {/* Main Content Area */}
      <article className="flex-1 min-w-0">
        {/* Breadcrumbs */}
        {docPath && (
          <Breadcrumbs
            organizationId={organizationId}
            docPath={docPath}
            currentTitle={doc?.title}
          />
        )}

        {/* Markdown Content (includes title as H1) */}
        <MarkdownRenderer content={doc?.content || ''} currentPath={doc?.path} />
      </article>

      {/* Table of Contents - Desktop Only */}
      {showToc && doc?.content && (
        <aside className="hidden lg:block flex-shrink-0">
          <TableOfContents content={doc.content} />
        </aside>
      )}
    </div>
  );

  return (
    <div
      className={cn('px-6 py-8 max-w-7xl mx-auto', className)}
      style={{ color: currentTheme?.['primary-text'] }}
    >
      {/* Loading State */}
      {loading && renderLoading()}

      {/* Not-found State — checked before the generic error state */}
      {!loading && notFound && renderNotFound()}

      {/* Error State */}
      {!loading && !notFound && error && renderError()}

      {/* Empty State */}
      {!loading && !error && !doc && renderEmpty()}

      {/* Content State */}
      {!loading && !error && doc && renderContent()}
    </div>
  );
}
